/**
 * useFocusOnRouteChange — déplace le focus dans la nouvelle vue à chaque
 * changement de contexte (SAAS-915).
 *
 * @remarks
 * Rien ne gérait le focus au changement de route : au clic sur un lien
 * interne, le focus restait sur l'élément démonté par React, retombant sur
 * `<body>`. Un lecteur d'écran n'annonçait rien ; un utilisateur clavier
 * repartait du haut du document à chaque navigation.
 *
 * ## La cible : le `<h1>` de la vue, pas un registre de titres
 *
 * On déplace le focus sur le premier `<h1>` du panneau `#main-content`. C'est
 * suffisant pour les deux besoins à la fois : un lecteur d'écran annonce le
 * contenu de ce qui reçoit le focus, ce n'est pas un mécanisme séparé à
 * construire (région `aria-live` ou équivalent).
 *
 * Aucune vue ne remonte de texte de titre au shell aujourd'hui — en
 * construire un registre central (`Context → libellé`) aurait recréé
 * exactement le défaut que SAAS-884 a corrigé : une liste manuelle qui
 * diverge du code au premier contexte ajouté sans y penser. Les vues rendent
 * déjà leur `<h1>` pour elles-mêmes ; le hook le réutilise.
 *
 * ## Pourquoi un `MutationObserver`
 *
 * Le panneau centre charge son contenu via `React.lazy` + `Suspense`. Au
 * premier passage sur une route dans la session, le changement de contexte
 * est immédiat mais le `<h1>` n'existe pas encore — le fallback de
 * chargement est affiché. Un délai fixe deviné serait soit trop court (le
 * focus rate la cible), soit trop long (l'utilisateur perçoit un flottement).
 * L'observer attend l'apparition réelle, sans borne arbitraire — le
 * `setTimeout` ci-dessous n'est qu'un filet en cas de vue sans `<h1>`.
 *
 * ## Ce qui est délibérément ignoré
 *
 * Le tout premier rendu (chargement initial de la page) ne déplace rien : le
 * focus par défaut du navigateur y est déjà correct, le lui voler surprendrait
 * quelqu'un qui vient d'arriver.
 */

import { useEffect, useRef } from 'react';
import type { Context } from '@/app/registry';

/** Au-delà, on considère la vue sans `<h1>` plutôt que toujours en chargement. */
const FALLBACK_DELAY_MS = 3000;

function focusElement(el: HTMLElement): void {
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
  el.focus();
}

export function useFocusOnRouteChange(context: Context): void {
  const isFirstRun = useRef(true);
  const previousContext = useRef(context);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      previousContext.current = context;
      return;
    }
    if (previousContext.current === context) return;
    previousContext.current = context;

    const container = document.getElementById('main-content');
    if (!container) return;

    const existingHeading = container.querySelector<HTMLHeadingElement>('h1');
    if (existingHeading) {
      focusElement(existingHeading);
      return;
    }

    const observer = new MutationObserver(() => {
      const heading = container.querySelector<HTMLHeadingElement>('h1');
      if (!heading) return;
      focusElement(heading);
      observer.disconnect();
    });
    observer.observe(container, { childList: true, subtree: true });

    // Repli : vue sans `<h1>`, ou chargement anormalement long — le panneau
    // reçoit le focus plutôt que de le laisser sur l'élément démonté.
    const fallback = window.setTimeout(() => {
      observer.disconnect();
      focusElement(container);
    }, FALLBACK_DELAY_MS);

    return () => {
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, [context]);
}
